import React, { useState } from "react"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import {
  Combobox,
  ComboboxInput as HeadlessComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react"

export type TComboboxOption = {
  id: number
  label: string
  value: string
}

export type TComboboxInputProps = {
  value: string
  onChange: (value: string) => void
  options: TComboboxOption[]
  placeholder?: string
  className?: string
  isError?: boolean
}

export default function ComboboxInput({
  value,
  onChange,
  options,
  placeholder = "",
  className = "",
  isError = false,
}: TComboboxInputProps) {
  const [query, setQuery] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const filteredOptions = React.useMemo(() => {
    if (query === "") return options

    return options.filter(option =>
      option.label.toLowerCase().includes(query.toLowerCase()),
    )
  }, [query, options])

  const getCurrentOption = () =>
    options.find(option => option.value === value) || {
      id: 0,
      label: value,
      value,
    }

  const handleChange = (option: TComboboxOption | null) => {
    if (option) {
      onChange(option.value)
      setQuery("")
    }
  }

  return (
    <div className="relative">
      <Combobox
        value={getCurrentOption()}
        onChange={handleChange}
        nullable
        by="value"
        as="div"
      >
        <HeadlessComboboxInput
          className={`w-full px-system-xs pr-8 py-system-xs rounded-system-md bg-system-blue-700 border border-system-blue-600 hover:border-system-blue-500 focus:border-system-cyan-400 h-system-2xl transition-all duration-200 focus:outline-none text-white placeholder-system-gray-500 ${
            isError
              ? "!border-system-error-400 focus:!border-system-error-400"
              : ""
          } ${className}`}
          displayValue={() => value}
          onChange={event => {
            event.preventDefault()
            const newValue = event.target.value
            setQuery(newValue)
            onChange(newValue)
          }}
          onBlur={() => setIsFocused(false)}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          onKeyDown={event => {
            if (event.key === "Enter") {
              event.preventDefault()
            }
          }}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ChevronDownIcon className="w-4 h-4 text-system-gray-400" />
        </div>
        <ComboboxOptions
          static={isFocused}
          data-open={isFocused}
          className="absolute top-full left-0 w-full z-50 mt-1 bg-system-blue-700 border border-system-blue-600 rounded-system-md shadow-lg max-h-60 overflow-y-auto data-[open=false]:hidden"
        >
          {filteredOptions.map(option => (
            <ComboboxOption
              key={option.id}
              value={option}
              className="px-system-xs py-system-xxs cursor-pointer hover:bg-system-blue-600 text-white text-sm transition-colors whitespace-nowrap data-[focus]:bg-system-blue-600"
            >
              {option.label}
            </ComboboxOption>
          ))}
          {query &&
            !filteredOptions.some(
              option => option.label.toLowerCase() === query.toLowerCase(),
            ) && (
              <ComboboxOption
                value={{
                  id: 0,
                  label: query,
                  value: query,
                }}
                className="px-system-xs py-system-xxs cursor-pointer hover:bg-system-blue-600 text-white text-sm transition-colors whitespace-nowrap data-[focus]:bg-system-blue-600 border-t border-system-blue-600"
              >
                Use "{query}"
              </ComboboxOption>
            )}
          <div className="px-system-xs py-system-xxs text-system-gray-400 text-xs border-t border-system-blue-600 whitespace-nowrap">
            Type custom value above
          </div>
        </ComboboxOptions>
      </Combobox>
    </div>
  )
}
